import { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Field, Stack, Button, tokens, Select, Text } from '@hotjar/hotsauce';
import { ColFormWrapper, Form } from '@hotjar/webapp/features/Registration/common/styles';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { trackExpertiseSubmitted } from '@hotjar/webapp/domains/activation/user-personalization-info/tracking';
import type { USER_EXPERTISE } from '@hotjar/webapp/domains/activation/user-personalization-info';
import { useOnboardingNavigation } from '../hooks/useOnboardingNavigation';
import { useExpertiseSelection } from '../../../hooks/useExpertiseSelection';

const FormWrapper = styled(Form)`
  max-width: 465px;
`;

export const StyledField = styled(Field)`
  width: 100%;
`;

const ButtonWrapper = styled.div``;

const ONBOARDING_EXPERTISE_STEP_UI_POSITION = 'onboarding-expertise';

type UserId = Parameters<ReturnType<typeof useOnboardingNavigation>['getOrgDetails']>[0];

export interface ExpertiseSelectionProps {
  onSubmit: (expertise?: USER_EXPERTISE) => void;
  userId: UserId;
  isSubmitInProgress?: boolean;
}

export const ExpertiseSelection = ({
  userId,
  onSubmit,
  isSubmitInProgress,
}: ExpertiseSelectionProps): JSX.Element => {
  const { t } = useTranslation();
  const { click: trackClick } = useTrack();
  const { getOrgDetails } = useOnboardingNavigation();
  const expertiseValues = useExpertiseSelection();
  const [selectedExpertise, setSelectedExpertise] = useState(
    getOrgDetails(userId)?.expertise || '',
  );
  const [isSkipInProgress, setIsSkipInProgress] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackExpertiseSubmitted({ expertise: selectedExpertise as USER_EXPERTISE }); // we keep the other tracking methods for now
    trackClick(selectedExpertise, ONBOARDING_EXPERTISE_STEP_UI_POSITION, {});
    trackClick('Expertise submitted', 'Next - Expertise Submitted', {});
    onSubmit(selectedExpertise as USER_EXPERTISE);
  };

  const handleSkip = () => {
    trackClick('skip', ONBOARDING_EXPERTISE_STEP_UI_POSITION, {});
    setIsSkipInProgress(true);
    onSubmit();
  };

  return (
    <ColFormWrapper data-testid="organization-steps-expertise">
      <FormWrapper onSubmit={handleSubmit} noValidate>
        <Text
          css={{ marginBottom: tokens.spacer.x1 }}
          as="h1"
          textType="header4"
          textColor="text_default"
          aria-live="assertive"
        >
          {t('activation:onboarding.organization-steps.expertise.title')}
        </Text>
        <Text
          as="p"
          css={{ marginBottom: tokens.spacer.x2 }}
          textType="body2"
          textColor="text_light"
        >
          {t('activation:onboarding.organization-steps.expertise.subtitle')}
        </Text>
        <Stack preset="form">
          <Select
            size="l"
            id="expertise-select"
            name="expertise-select"
            isRequired
            data-testid="expertise-select"
            defaultValue={getOrgDetails(userId)?.expertise || ''}
            autoFocus
            onChange={(e) => setSelectedExpertise(e.target.value)}
          >
            <option value="" disabled>
              {t('activation:onboarding.organization-steps.select-placeholder')}
            </option>
            {expertiseValues.map(({ value, label }) => (
              <option value={value} key={value}>
                {t(label)}
              </option>
            ))}
          </Select>
          <ButtonWrapper>
            <Button
              css={{ marginRight: tokens.spacer.x2 }}
              size="l"
              type="submit"
              id="submit-step-expertise"
              data-testid="submit-step-expertise"
              isLoading={isSubmitInProgress && !isSkipInProgress}
              onClick={handleSubmit}
              isDisabled={!selectedExpertise || isSkipInProgress}
              loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
            >
              {t('activation:onboarding.organization-steps.submit-cta')}
            </Button>
            <Button
              size="l"
              kind="secondary"
              id="skip-step-expertise"
              data-testid="skip-step-expertise"
              isLoading={isSkipInProgress}
              onClick={handleSkip}
              loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
            >
              {t('activation:onboarding.organization-steps.skip-cta')}
            </Button>
          </ButtonWrapper>
        </Stack>
      </FormWrapper>
    </ColFormWrapper>
  );
};
